/**
 * Detects an in-progress @-mention so the picker knows when to open and what to
 * replace. A trigger is an "@" at the start of a line or after whitespace, followed by
 * the (possibly empty) query typed so far.
 */

const MENTION_TRIGGER = /(?:^|\s)@([^\s@]*)$/;

/** Longest query we keep the picker open for; past this the user is writing prose. */
const MAX_QUERY_LENGTH = 40;

export interface MentionTrigger {
  /** Text typed after the "@". */
  query: string;
  /** Document index of the "@" itself. */
  index: number;
  /** Length of "@" plus the query — what selecting a user replaces. */
  length: number;
}

/**
 * `textBeforeCursor` comes from Quill's `getText`, which omits embeds, so its length
 * cannot be used as a document index. The "@" and its query are always plain text
 * contiguous with the cursor, though, so their length is the same in both coordinate
 * systems and the document index can be measured back from `cursorIndex`.
 */
export function findMentionTrigger(
  textBeforeCursor: string,
  cursorIndex: number
): MentionTrigger | null {
  const match = MENTION_TRIGGER.exec(textBeforeCursor);
  if (!match) return null;

  const query = match[1];
  if (query.length > MAX_QUERY_LENGTH) return null;

  const length = query.length + 1;
  return { query, index: cursorIndex - length, length };
}
